import { useEffect, useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { Box, TextField, Tooltip } from '@mui/material';
import { Facet } from '../shared/Facet';
import { addPropAndValue, addToSelectedList } from '../shared/UtilFunctions';
import {
  MGC_FILTER_CODE,
  MGC_FILTER_DESC,
  MGC_FILTER_HIERARCHY
} from '../shared/AppConstants';
import MGCHierarchyFilter from './MGCHierarchyFilter';

function getNextToken(str, startLoc, endLocExcluding, lookFurther) {
  let subStr = '';
  let expr = /0{2}/;

  if (str.length >= endLocExcluding)
    subStr = str.substring(startLoc, endLocExcluding);
  else return [subStr, subStr];

  // Invalid if it contains two consecutive zeros
  let invalid = expr.test(subStr);

  if (lookFurther && invalid && endLocExcluding < str.length)
    return getNextToken(
      str.substring(endLocExcluding),
      startLoc,
      endLocExcluding,
      lookFurther
    );
  else return [str.substring(endLocExcluding), invalid ? '' : subStr];
}

export function getMGCHierarchyFromCode(mgcSearchCode) {
  let json = {};
  let str = getNextToken(mgcSearchCode, 0, 3);

  if (str[1] !== '') {
    addPropAndValue(json, 'commodities', [str[1]]);

    str = getNextToken(str[0], 0, 2, true);
    if (str[1] !== '') addPropAndValue(json, 'majors', [str[1]]);

    str = getNextToken(str[0], 0, 2, true);
    if (str[1] !== '') addPropAndValue(json, 'minors', [str[1]]);

    str = getNextToken(str[0], 0, 2, true);
    if (str[1] !== '') addPropAndValue(json, 'segments', [str[1]]);
  }

  return json;
}

export default function MGCFilter(props) {
  const { masterMGCJson, selectedMGCList } = props;
  const parentSetSelectedMGCs = props.setSelectedMGCs;
  const [selectedMGCs, setSelectedMGCs] = useState([]);
  const [mgcFilterType, setMgcFilterType] = useState(MGC_FILTER_HIERARCHY);
  const [mgcSearchText, setMgcSearchText] = useState('');
  const [mgcSearchCode, setMgcSearchCode] = useState('');
  const [mgcSearchCodeError, setMgcSearchCodeError] = useState(false);
  const [localSelectedMGCList, setLocalSelectedMGCList] = useState(null); // State for Hierarchical filter

  function getCode(list) {
    return list && list.length > 0 ? list.join(',') : '';
  }
  function getMGCCodeFromHierarchy(json) {
    return (
      getCode(json.commodities) +
      getCode(json.majors) +
      getCode(json.minors) +
      getCode(json.segments)
    );
  }
  function setParentFilterInfo(hierarchyJson) {
    let json = {};
    let selectedItems = [];

    if (mgcFilterType === MGC_FILTER_HIERARCHY) {
      if (hierarchyJson) json = hierarchyJson;
      // List was set by the Hierarchy Filter already
      else setSelectedMGCs(selectedItems);
    } else {
      if (mgcFilterType === MGC_FILTER_CODE) {
        if (!mgcSearchCodeError) json = getMGCHierarchyFromCode(mgcSearchCode);
        addToSelectedList(selectedItems, mgcSearchCode, 'Code', true);
      } else if (mgcFilterType === MGC_FILTER_DESC) {
        let searchText = mgcSearchText ? mgcSearchText.trim() : '';

        if (searchText !== '') {
          addToSelectedList(selectedItems, searchText, 'Description', true);
          json = { mgcFilterText: searchText };
        }
      }
      setSelectedMGCs(selectedItems);
    }

    addPropAndValue(json, 'mgcFilterType', mgcFilterType);
    parentSetSelectedMGCs(json);
  }

  useEffect(() => {
    if (selectedMGCList) {
      setMgcFilterType(selectedMGCList.mgcFilterType);
      switch (selectedMGCList.mgcFilterType) {
        case MGC_FILTER_CODE:
          setMgcSearchCode(getMGCCodeFromHierarchy(selectedMGCList));
          break;
        case MGC_FILTER_DESC:
          setMgcSearchText(selectedMGCList.mgcFilterText);
          break;
        case MGC_FILTER_HIERARCHY:
          setLocalSelectedMGCList(selectedMGCList);
          break;
      }
    } else setDefaultState(MGC_FILTER_HIERARCHY);
  }, [selectedMGCList]);

  useEffect(
    () => setParentFilterInfo(),
    [mgcFilterType, mgcSearchText, mgcSearchCode]
  );

  function checkAndSetMGCCode(val, replaceBlanks) {
    const regEx = /^[dDnN]{1}[0-9 ]{2,}$/;
    let code = (replaceBlanks ? val.replaceAll(' ', '') : val).toUpperCase();
    let valid = regEx.test(code);

    setMgcSearchCode(code);
    setMgcSearchCodeError(!valid);
  }
  function setDefaultState(filterOption) {
    setMgcFilterType(filterOption);
    setLocalSelectedMGCList(null);
    setMgcSearchText('');
    setMgcSearchCode('');
  }

  const hasSelectedMGCs = selectedMGCs.some(
    (item) => item?.parent || item?.children?.length > 0
  );

  return (
    <Facet defaultActiveKey="mgc_filter">
      <Facet.Item
        id="mgc_filter"
        title="Material Group Code"
        openByDefault={true}
        selectedItems={[]}
        selectedCount={hasSelectedMGCs ? 1 : 0}
        clearSelection={() => setDefaultState(null)}
      >
        <>
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'row',
              gap: theme.fuel6Tokens.JddcSpacingSmall
            })}
          >
            <FormControlLabel
              control={
                <Radio
                  id="custom-radio-1"
                  name="mgcFilterType"
                  value={MGC_FILTER_HIERARCHY}
                  defaultChecked={mgcFilterType === MGC_FILTER_HIERARCHY}
                  checked={mgcFilterType === MGC_FILTER_HIERARCHY}
                  onClick={() => setDefaultState(MGC_FILTER_HIERARCHY)}
                />
              }
              label="Hierarchy"
              aria-label="MGC Hierarchy"
            />
            <FormControlLabel
              control={
                <Radio
                  id="custom-radio-3"
                  name="mgcFilterType"
                  value={MGC_FILTER_CODE}
                  defaultChecked={mgcFilterType === MGC_FILTER_CODE}
                  checked={mgcFilterType === MGC_FILTER_CODE}
                  onClick={() => setDefaultState(MGC_FILTER_CODE)}
                />
              }
              label="MGC"
              aria-label="MGC Code"
            />
            <FormControlLabel
              control={
                <Radio
                  id="custom-radio-2"
                  name="mgcFilterType"
                  value={MGC_FILTER_DESC}
                  defaultChecked={mgcFilterType === MGC_FILTER_DESC}
                  checked={mgcFilterType === MGC_FILTER_DESC}
                  onClick={() => setDefaultState(MGC_FILTER_DESC)}
                />
              }
              label="Description"
              aria-label="MGC Description"
            />
          </Box>
          <Box sx={(theme) => ({ pt: theme.fuel6Tokens.JddcSpacingSmall })}>
            {mgcFilterType === MGC_FILTER_CODE && (
              <Tooltip
                title={
                  mgcSearchCodeError
                    ? 'Minimum 3 characters required, beginning with D.'
                    : ''
                }
              >
                <TextField
                  fullWidth
                  placeholder="Search by Material Group Code"
                  maxLength="12"
                  value={mgcSearchCode}
                  onChange={(e) => checkAndSetMGCCode(e.target.value)}
                  onBlur={(e) => checkAndSetMGCCode(e.target.value, true)}
                />
              </Tooltip>
            )}
            {mgcFilterType === MGC_FILTER_DESC && (
              <TextField
                fullWidth
                placeholder="Search by description"
                value={mgcSearchText}
                onChange={(e) => setMgcSearchText(e.target.value)}
                onBlur={(e) => setMgcSearchText(e.target.value.trim())}
              />
            )}
            {mgcFilterType === MGC_FILTER_HIERARCHY && (
              <MGCHierarchyFilter
                setParentFilterInfo={setParentFilterInfo}
                setSelectedMGCs={setSelectedMGCs}
                masterMGCJson={masterMGCJson}
                selectedMGCList={localSelectedMGCList}
              />
            )}
          </Box>
        </>
      </Facet.Item>
    </Facet>
  );
}
